import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  TextField, 
  Box,
  CircularProgress,
  Button,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { format, addDays, subDays } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BASE_URL = 'https://cctexasfinance.com:9090/report';

const useDataFetch = (url, date) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNoData, setIsNoData] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setIsNoData(false);
    try {
      const response = await fetch(`${url}?date=${date}`);
      if (response.status === 404) {
        setIsNoData(true);
        return;
      }
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const json = await response.json();
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [url, date]);

  return { data, loading, error, isNoData, retry: fetchData };
};

const LoadingState = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
    <CircularProgress />
  </Box>
);

const ErrorState = ({ onRetry }) => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
    <Button 
      variant="contained" 
      color="primary" 
      onClick={onRetry}
      sx={{ textTransform: 'none' }}
    >
      Retry
    </Button>
  </Box>
);

const NoDataState = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
    <Typography variant="body1" color="text.secondary">
      No data available for this date
    </Typography>
  </Box>
);

const theme = createTheme({
  palette: {
    primary: {
      main: '#1a73e8',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
    h4: {
      fontWeight: 500,
      marginBottom: '16px',
      textAlign: 'center',
    },
    body1: {
      marginBottom: '24px',
      textAlign: 'center',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          color: 'black',
          boxShadow: '0 1px 3px 0 rgba(60, 64, 67, 0.3)',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '64px !important',
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderRight: '1px solid rgba(224, 224, 224, 1)',
          '&:last-child': {
            borderRight: 'none',
          },
          padding: '8px',
          fontSize: '0.875rem',
        },
        head: {
          fontWeight: 'bold',
          backgroundColor: '#f1f3f4',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td, &:last-child th': {
            borderBottom: 0,
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            transition: 'background-color 0.3s ease',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(60, 64, 67, 0.3), 0 4px 8px 3px rgba(60, 64, 67, 0.15)',
          borderRadius: '8px',
          overflow: 'hidden',
          marginBottom: '24px',
        },
      },
    },
  },
});

const DateNavigator = ({ selectedDate, onDateChange }) => {
  const handlePreviousDay = () => {
    const newDate = subDays(new Date(selectedDate), 0);
    onDateChange(format(newDate, 'yyyy-MM-dd'));
  };

  const handleNextDay = () => {
    const newDate = addDays(new Date(selectedDate), 2);
    onDateChange(format(newDate, 'yyyy-MM-dd'));
  };

  return (
    <Box 
      display="flex" 
      alignItems="center" 
      justifyContent="center"
      gap={2}
      sx={{ 
        width: '100%',
        position: 'relative'
      }}
    >
      <IconButton 
        onClick={handlePreviousDay} 
        size="small"
        sx={{
          padding: '8px'
        }}
      >
        <ChevronLeft size={24} />
      </IconButton>
      
      <TextField
        type="date"
        value={selectedDate}
        onChange={(e) => onDateChange(e.target.value)}
        sx={{
          width: '160px',
          marginTop:'20px',
          '& .MuiInputBase-root': {
            height: '40px'
          }
        }}
        InputProps={{
          sx: {
            '& input': {
              textAlign: 'center',
              padding: '8px 14px',
              fontSize: '1rem',
              cursor: 'pointer'
            }
          }
        }}
      />

      <IconButton 
        onClick={handleNextDay} 
        size="small"
        sx={{
          padding: '8px'
        }}
      >
        <ChevronRight size={24} />
      </IconButton>
    </Box>
  );
};

const ReservoirStatistics = () => {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  const resStats = useDataFetch(`${BASE_URL}/res_stats`, selectedDate);
  const coloradoData = useDataFetch(`${BASE_URL}/colorado`, selectedDate);
  const flowsData = useDataFetch(`${BASE_URL}/flows`, selectedDate);
  const texanaData = useDataFetch(`${BASE_URL}/tex`, selectedDate);
  const weatherData = useDataFetch(`${BASE_URL}/weather`, selectedDate);
  const passthruData = useDataFetch(`${BASE_URL}/passthru`, selectedDate);

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  const calculatePercentage = (current, full) => {
    if (!current?.volume || !full?.volume) return 0;
    return ((Number(current.volume) / Number(full.volume)) * 100).toFixed(2) || 0;
  };

  const calculateCombined = (ccr = {}, lcc = {}) => {
    const ccrVolume = Number(ccr.volume || 0);
    const lccVolume = Number(lcc.volume || 0);
    const totalVolume = ccrVolume + lccVolume;

    const ccrCapacity = Number(ccr.cap || 0);
    const lccCapacity = Number(lcc.cap || 0);
    const totalCapacity = ccrCapacity + lccCapacity;

    return {
      volume: totalVolume.toLocaleString(),
      capacity: `${totalCapacity.toFixed(2) || 0}%`
    };
  };

  const renderReservoirStatistics = () => {
    if (resStats.loading) return <LoadingState />;
    if (resStats.isNoData) return <NoDataState />;
    if (resStats.error) return <ErrorState onRetry={resStats.retry} />;

    const fullData = resStats.data?.Full || {};
    
    // Prepare full data row
    const fullCcr = {
      ...fullData.ccr,
      cap: 100  // Full capacity is 100%
    };

    const fullLcc = {
      ...fullData.lcc,
      cap: 100  // Full capacity is 100%
    };

    const fullTex = {
      ...fullData.tex,
      cap: 100  // Full capacity is 100%
    };

    const fullCombined = calculateCombined(fullCcr, fullLcc);

    // Get all dates except 'Full'
    const dates = Object.keys(resStats.data || {}).filter(key => key !== 'Full');

    return (
      <TableBody>
        {/* Full capacity row */}
        <TableRow>
          <TableCell>Full Capacity</TableCell>
          <TableCell align="right">{fullCcr.elevation || 0}</TableCell>
          <TableCell align="right">{fullCcr.volume?.toLocaleString() || 0}</TableCell>
          <TableCell align="right">{fullCcr.cap || 0}%</TableCell>
          <TableCell align="right">{fullLcc.elevation || 0}</TableCell>
          <TableCell align="right">{fullLcc.volume?.toLocaleString() || 0}</TableCell>
          <TableCell align="right">{fullLcc.cap || 0}%</TableCell>
          <TableCell align="right">{fullCombined.volume || 0}</TableCell>
          <TableCell align="right">{fullCombined.capacity || '100%'}</TableCell>
          <TableCell align="right">{fullTex.elevation || 0}</TableCell>
          <TableCell align="right">{fullTex.volume?.toLocaleString() || 0}</TableCell>
          <TableCell align="right">{fullTex.cap || 0}%</TableCell>
        </TableRow>

        {/* Date rows */}
        {dates.map(date => {
          const dateData = resStats.data[date] || {};
          
          const ccr = dateData.ccr ? {
            ...dateData.ccr,
            cap: calculatePercentage(dateData.ccr, fullData.ccr)
          } : { elevation: 0, volume: 0, cap: 0 };

          const lcc = dateData.lcc ? {
            ...dateData.lcc,
            cap: calculatePercentage(dateData.lcc, fullData.lcc)
          } : { elevation: 0, volume: 0, cap: 0 };

          const tex = dateData.tex ? {
            ...dateData.tex,
            cap: calculatePercentage(dateData.tex, fullData.tex)
          } : { elevation: 0, volume: 0, cap: 0 };

          const combined = calculateCombined(ccr, lcc);

          return (
            <TableRow key={date}>
              <TableCell>{date}</TableCell>
              <TableCell align="right">{ccr.elevation || 0}</TableCell>
              <TableCell align="right">{ccr.volume?.toLocaleString() || 0}</TableCell>
              <TableCell align="right">{ccr.cap || 0}%</TableCell>
              <TableCell align="right">{lcc.elevation || 0}</TableCell>
              <TableCell align="right">{lcc.volume?.toLocaleString() || 0}</TableCell>
              <TableCell align="right">{lcc.cap || 0}%</TableCell>
              <TableCell align="right">{combined.volume || 0}</TableCell>
              <TableCell align="right">{combined.capacity || '0%'}</TableCell>
              <TableCell align="right">{tex.elevation || 0}</TableCell>
              <TableCell align="right">{tex.volume?.toLocaleString() || 0}</TableCell>
              <TableCell align="right">{tex.cap || 0}%</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    );
  };

  const renderColoradoRiver = () => {
    if (coloradoData.loading) return <LoadingState />;
    if (coloradoData.isNoData) return <NoDataState />;
    if (coloradoData.error) return <ErrorState onRetry={coloradoData.retry} />;
    
    const data = coloradoData.data?.[selectedDate] || {};
    const mtd = coloradoData.data?.MTD || {};
    const ytd = coloradoData.data?.YTD || {};

    return (
      <TableBody>
        <TableRow>
          <TableCell>{selectedDate}</TableCell>
          <TableCell>{data.intake || 0}</TableCell>
          <TableCell>{mtd.intake || 0}</TableCell>
          <TableCell>{ytd.intake || 0}</TableCell>
          <TableCell>{data.flow || 0}</TableCell>
          <TableCell>{mtd.flow || 0}</TableCell>
          <TableCell>{data.gague_time ? format(new Date(data.gague_time), 'yyyy-MM-dd') : ''}</TableCell>
          <TableCell>{data.gague_time ? format(new Date(data.gague_time), 'HH:mm') : ''}</TableCell>
          <TableCell>{data.gague?.toFixed(2) || '0.00'}</TableCell>
        </TableRow>
      </TableBody>
    );
  };

  const renderFlows = () => {
    if (flowsData.loading) return <LoadingState />;
    if (flowsData.isNoData) return <NoDataState />;
    if (flowsData.error) return <ErrorState onRetry={flowsData.retry} />;
  
    const data = flowsData.data?.[selectedDate] || {};
    const mtd = flowsData.data?.MTD || {};
  
    const flowStations = [
      {
        id: 'ntrt',
        name: 'Nueces River at Three Rivers, Texas',
        link: 'https://waterdata.usgs.gov/tx/nwis/dv/?site_no=08210000&PARAmeter_cd=00060,00065'
      },
      {
        id: 'nrtt',
        name: 'Nueces River at Tilden, Texas',
        link: 'https://waterdata.usgs.gov/tx/nwis/dv/?site_no=08194500&PARAmeter_cd=00060,00065'
      },
      {
        id: 'fttr',
        name: 'Frio River at Tilden, Texas',
        link: 'https://waterdata.usgs.gov/tx/nwis/dv/?site_no=08206600&PARAmeter_cd=00060,00065'
      },
      {
        id: 'smtt',
        name: 'San Miguel Creek at Tilden, Texas',
        link: 'https://waterdata.usgs.gov/tx/nwis/dv/?site_no=08206700&PARAmeter_cd=00060,00065'
      },
      {
        id: 'arwt',
        name: 'Atascosa River at Whitsett, Texas',
        link: 'https://waterdata.usgs.gov/tx/nwis/dv/?site_no=08208000&PARAmeter_cd=00060,00065'
      },
      {
        id: 'ccr',
        name: 'Release from Choke Canyon Reservoir',
        link: 'https://waterdata.usgs.gov/monitoring-location/08206910/#parameterCode=00065&period=P7D'
      },
      {
        id: 'nrmt',
        name: 'Nueces River at Mathis, Texas (La Fruta Bridge)',
        link: 'https://waterdata.usgs.gov/tx/nwis/dv/?site_no=08211000&PARAmeter_cd=00060,00065'
      },
      {
        id: 'ncat',
        name: 'Nueces River at Calallen, Texas',
        link: 'https://waterdata.usgs.gov/tx/nwis/dv/?site_no=08211500&PARAmeter_cd=00060,00065'
      },
      {
        id: 'rbp',
        name: 'Rincon Bayou Pipeline',
        link: ''
      }
    ];
  
    return (
      <TableBody>
        {flowStations.map((station) => (
          <TableRow key={station.id}>
            <TableCell>
              {station.link ? (
                <a 
                  href={station.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {station.id.toUpperCase()}
                </a>
              ) : (
                station.id.toUpperCase()
              )}
            </TableCell>
            <TableCell>{station.name}</TableCell>
            <TableCell align="right">
              {(data[station.id] || 0).toLocaleString()}
            </TableCell>
            <TableCell align="right">
              {(mtd[station.id] || 0).toLocaleString()}
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell colSpan={2}>Computed as: NTRT+FRTT+SMTT-CCR</TableCell>
          <TableCell align="right">
            {(data.inflow || 0).toLocaleString()}
          </TableCell>
          <TableCell align="right">
            {(mtd.inflow || 0).toLocaleString()}
          </TableCell>
        </TableRow>
      </TableBody>
    );
  };

  const renderTexanaSupply = () => {
    if (texanaData.loading) return <LoadingState />;
    if (texanaData.isNoData) return <NoDataState />;
    if (texanaData.error) return <ErrorState onRetry={texanaData.retry} />;

    const data = texanaData.data?.[selectedDate] || {};
    const mtd = texanaData.data?.MTD || {};

    return (
      <TableBody>
        <TableRow>
          <TableCell>{selectedDate}</TableCell>
          <TableCell>{data.intake || 0}</TableCell>
          <TableCell>{mtd.intake || 0}</TableCell>
          <TableCell>{data.intake_un || 0}</TableCell>
          <TableCell>{data.intake_int || 0}</TableCell>
          <TableCell>{mtd.intake_un || 0}</TableCell>
          <TableCell>{data.intake_int || 0}</TableCell>
          <TableCell>{mtd.intake_int || 0}</TableCell>
          <TableCell>{data.intake_un || 0}</TableCell>
        </TableRow>
      </TableBody>
    );
  };

  const renderWeather = () => {
    if (weatherData.loading) return <LoadingState />;
    if (weatherData.isNoData) return <NoDataState />;
    if (weatherData.error) return <ErrorState onRetry={weatherData.retry} />;

    // Obtener la fecha actual (la primera fecha que no sea MTD o YTD)
    const currentDate = Object.keys(weatherData.data || {})
        .find(key => key !== 'MTD' && key !== 'YTD') || '';

    const data = weatherData.data?.[currentDate] || {};
    const mtd = weatherData.data?.MTD || {};
    const ytd = weatherData.data?.YTD || {};

    const ccr = data.ccr || {};
    const lcc = data.lcc || {};
    const ccrMtd = mtd.ccr || {};
    const lccMtd = mtd.lcc || {};
    const ccrYtd = ytd.ccr || {};
    const lccYtd = ytd.lcc || {};

    // Función para calcular el combined
    const calculateCombined = (val1 = 0, val2 = 0) => (val1 + val2);

    return (
      <>
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2}></TableCell>
            <TableCell colSpan={3} align="center">Choke Canyon Reservoir</TableCell>
            <TableCell colSpan={3} align="center">Lake Corpus Christi</TableCell>
            <TableCell colSpan={3} align="center">CCR/LCC Combined</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{currentDate}</TableCell>
            <TableCell>MTD</TableCell>
            <TableCell>YTD</TableCell>
            <TableCell>{currentDate}</TableCell>
            <TableCell>MTD</TableCell>
            <TableCell>YTD</TableCell>
            <TableCell>{currentDate}</TableCell>
            <TableCell>MTD</TableCell>
            <TableCell>YTD</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Air Temp</TableCell>
            <TableCell>{ccr.air_temp || 0}</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell>{lcc.air_temp || 0}</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Evaporation</TableCell>
            <TableCell>{ccr.evaporation || 0}</TableCell>
            <TableCell>{ccrMtd.evaporation || 0}</TableCell>
            <TableCell>{ccrYtd.evaporation || 0}</TableCell>
            <TableCell>{lcc.evaporation || 0}</TableCell>
            <TableCell>{lccMtd.evaporation || 0}</TableCell>
            <TableCell>{lccYtd.evaporation || 0}</TableCell>
            <TableCell>{calculateCombined(ccr.evaporation, lcc.evaporation)}</TableCell>
            <TableCell>{calculateCombined(ccrMtd.evaporation, lccMtd.evaporation)}</TableCell>
            <TableCell>{calculateCombined(ccrYtd.evaporation, lccYtd.evaporation)}</TableCell>
          </TableRow>
          <TableRow>
          <TableCell>Rainfall</TableCell>
          <TableCell>{typeof ccr.rainfall === 'number' ? ccr.rainfall.toFixed(2) : "0.00"}</TableCell>
          <TableCell>{typeof ccrMtd.rainfall === 'number' ? ccrMtd.rainfall.toFixed(2) : "0.00"}</TableCell>
          <TableCell>{typeof ccrYtd.rainfall === 'number' ? ccrYtd.rainfall.toFixed(2) : "0.00"}</TableCell>
          <TableCell>{typeof lcc.rainfall === 'number' ? lcc.rainfall.toFixed(2) : "0.00"}</TableCell>
          <TableCell>{typeof lccMtd.rainfall === 'number' ? lccMtd.rainfall.toFixed(2) : "0.00"}</TableCell>
          <TableCell>{typeof lccYtd.rainfall === 'number' ? lccYtd.rainfall.toFixed(2) : "0.00"}</TableCell>
          <TableCell>{typeof calculateCombined(ccr.rainfall || 0, lcc.rainfall || 0) === 'number' ? calculateCombined(ccr.rainfall || 0, lcc.rainfall || 0).toFixed(2) : "0.00"}</TableCell>
          <TableCell>{typeof calculateCombined(ccrMtd.rainfall || 0, lccMtd.rainfall || 0) === 'number' ? calculateCombined(ccrMtd.rainfall || 0, lccMtd.rainfall || 0).toFixed(2) : "0.00"}</TableCell>
          <TableCell>{typeof calculateCombined(ccrYtd.rainfall || 0, lccYtd.rainfall || 0) === 'number' ? calculateCombined(ccrYtd.rainfall || 0, lccYtd.rainfall || 0).toFixed(2) : "0.00"}</TableCell>
        </TableRow>
        </TableBody>
      </>
    );
  };

  const renderPassthruTable = () => {
    if (passthruData.loading) return <LoadingState />;
    if (passthruData.isNoData) return <NoDataState />;
    if (passthruData.error) return <ErrorState onRetry={passthruData.retry} />;
  
    const data = passthruData.data || {};
  
    return (
      <TableBody>
        {/* Primera fila */}
        <TableRow>
          <TableCell rowSpan={2}>Target Passthru</TableCell>
          <TableCell>Monthly Target</TableCell>
          <TableCell align="right">{data.monthly_target?.toFixed(2) || 0}</TableCell>
          <TableCell align="right">0</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Salinity Relief Credit</TableCell>
          <TableCell align="right">Effective</TableCell>
          <TableCell align="right">0</TableCell>
        </TableRow>
  
        {/* Segunda fila */}
        <TableRow>
          <TableCell>Credit / -Deficit From Previous Month</TableCell>
          <TableCell>Date Deficit Satisfied</TableCell>
          <TableCell align="right">{selectedDate}</TableCell>
          <TableCell align="right">{data.deficit || 0}</TableCell>
        </TableRow>
  
        {/* Tercera fila */}
        <TableRow>
          <TableCell>Return Flow Credit</TableCell>
          <TableCell>Effective</TableCell>
          <TableCell align="right">{selectedDate}</TableCell>
          <TableCell align="right">{data.return_flow_credit || 0}</TableCell>
        </TableRow>
  
        {/* Cuarta fila */}
        <TableRow>
          <TableCell rowSpan={2}>Required Passthru</TableCell>
          <TableCell>Monthly Target</TableCell>
          <TableCell align="right">0</TableCell>
          <TableCell align="right">0</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Reservoir Inflow</TableCell>
          <TableCell align="right">1</TableCell>
          <TableCell align="right">{data.r_inflow?.toFixed(2) || 0}</TableCell>
        </TableRow>
  
        {/* Quinta fila */}
        <TableRow>
          <TableCell colSpan={3}>Estuary Inflows (NCAT + RBP)</TableCell>
          <TableCell align="right">{data.e_inflow?.toFixed(2) || 0}</TableCell>
        </TableRow>
  
        {/* Sexta fila */}
        <TableRow>
          <TableCell colSpan={3}>Passthru Surplus / -Deficit</TableCell>
          <TableCell align="right">{data.passthru?.toFixed(2) || 0}</TableCell>
        </TableRow>
      </TableBody>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ paddingTop: '64px' }}>
        <AppBar position="fixed">
          <Toolbar>
            <DateNavigator 
              selectedDate={selectedDate} 
              onDateChange={handleDateChange}
            />
          </Toolbar>
        </AppBar>

        {/* Reservoir Statistics */}
        <Paper elevation={3} sx={{ margin: 3, padding: 3 }}>
          <Typography variant="h4">Reservoir Statistics</Typography>
          <Typography variant="body1">
            Effective June 24, 2017, updated total reservoir capacities for Choke Canyon 
            Reservoir (2012 TWDB Volumetric Survey) and Lake Corpus Christi (2016 TWDB 
            Volumetric Survey) are being reflected in the Daily Reservoir System and 
            Pass-Thru Status Report
          </Typography>
          <TableContainer>
            <Table sx={{ minWidth: 650, borderCollapse: 'separate', borderSpacing: 0 }} 
                  size="small" 
                  aria-label="reservoir statistics table">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="center" colSpan={3}>Choke Canyon Reservoir</TableCell>
                  <TableCell align="center" colSpan={3}>Lake Corpus Christi</TableCell>
                  <TableCell align="center" colSpan={2}>CCR/LCC Combined</TableCell>
                  <TableCell align="center" colSpan={3}>Lake Texana</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell align="right">Elevation</TableCell>
                  <TableCell align="right">Volume</TableCell>
                  <TableCell align="right">% Capacity</TableCell>
                  <TableCell align="right">Elevation</TableCell>
                  <TableCell align="right">Volume</TableCell>
                  <TableCell align="right">% Capacity</TableCell>
                  <TableCell align="right">Volume</TableCell>
                  <TableCell align="right">% Capacity</TableCell>
                  <TableCell align="right">Elevation</TableCell>
                  <TableCell align="right">Volume</TableCell>
                  <TableCell align="right">% Capacity</TableCell>
                </TableRow>
              </TableHead>
              {renderReservoirStatistics()}
            </Table>
          </TableContainer>
        </Paper>

        {/* Colorado River */}
        <Paper elevation={3} sx={{ margin: 3, padding: 3 }}>
          <Typography variant="h4">Colorado River Water Supply</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Daily Intake</TableCell>
                  <TableCell>MTD</TableCell>
                  <TableCell>YTD</TableCell>
                  <TableCell>Daily Flow</TableCell>
                  <TableCell>MTD Flow</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Gage Height</TableCell>
                </TableRow>
              </TableHead>
              {renderColoradoRiver()}
            </Table>
          </TableContainer>
        </Paper>

        {/* Lake Texana Water Supply */}
        <Paper elevation={3} sx={{ margin: 3, padding: 3 }}>
          <Typography variant="h4">Lake Texana Water Supply</Typography>
          <Typography variant="body1">
            The split between Non-Interruptible and Interruptible amounts are updated monthly, 
            usually the first working day of the month
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell colSpan={9} align="center">LAKE TEXANA WATER SUPPLY*</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell rowSpan={2}>Date</TableCell>
                  <TableCell rowSpan={2}>Daily Intake</TableCell>
                  <TableCell rowSpan={2}>MTD</TableCell>
                  <TableCell colSpan={3} align="center">Non-Interruptible</TableCell>
                  <TableCell colSpan={3} align="center">Interruptible</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell>YTD</TableCell>
                  <TableCell>Remaining</TableCell>
                  <TableCell>Month</TableCell>
                  <TableCell>YTD</TableCell>
                  <TableCell>Remaining</TableCell>
                </TableRow>
              </TableHead>
              {renderTexanaSupply()}
            </Table>
          </TableContainer>
        </Paper>

        {/* Weather Related Information */}
        <Paper elevation={3} sx={{ margin: 3, padding: 3 }}>
          <Typography variant="h4">Weather Related Information</Typography>
          <TableContainer>
            <Table size="small">
              {renderWeather()}
            </Table>
          </TableContainer>
        </Paper>

        {/* Stream Flows */}
        <Paper elevation={3} sx={{ margin: 3, padding: 3 }}>
          <Typography variant="h4">Stream Flows</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Gauging Station</TableCell>
                  <TableCell align="right">Today</TableCell>
                  <TableCell align="right">MTD</TableCell>
                </TableRow>
              </TableHead>
              {renderFlows()}
            </Table>
          </TableContainer>
        </Paper>

        {/* Pass-Thru Status */}
        <Paper elevation={3} sx={{ margin: 3, padding: 3 }}>
  <Typography variant="h4">Estuary Inflows and Passthru Requirements</Typography>
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Category</TableCell>
          <TableCell>Description</TableCell>
          <TableCell>Details</TableCell>
          <TableCell align="right">Value</TableCell>
        </TableRow>
      </TableHead>
      {renderPassthruTable()}
    </Table>
  </TableContainer>
</Paper>
        
      </Box>
    </ThemeProvider>
  );
};

export default ReservoirStatistics;